/**
 * i18n: English / Urdu / Arabic with RTL. Strings are written inline in English:
 *   t('inbox.empty', 'No conversations yet')
 * Translations live in src/i18n/*.ts files, each exporting `{ ur: {...}, ar: {...} }`; all files are merged
 * automatically, so features can ship their own translation file.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Locale = 'en' | 'ur' | 'ar';
export const LOCALES: { code: Locale; label: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ur', label: 'اردو', dir: 'rtl' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
];

const modules = import.meta.glob('../i18n/*.ts', { eager: true }) as Record<string, { default: Partial<Record<Locale, Record<string, string>>> }>;
const dict: Record<Locale, Record<string, string>> = { en: {}, ur: {}, ar: {} };
Object.values(modules).forEach((m) => {
  (['ur', 'ar', 'en'] as Locale[]).forEach((l) => Object.assign(dict[l], m.default?.[l] ?? {}));
});

type Ctx = { locale: Locale; dir: 'ltr' | 'rtl'; setLocale: (l: Locale) => void; t: (key: string, fallback: string, vars?: Record<string, string | number>) => string };
const I18nCtx = createContext<Ctx | null>(null);

function interpolate(s: string, vars?: Record<string, string | number>) {
  return vars ? s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`)) : s;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try { return (localStorage.getItem('bf_locale') as Locale) || 'en'; } catch { return 'en'; }
  });
  const dir = LOCALES.find((l) => l.code === locale)?.dir ?? 'ltr';
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try { localStorage.setItem('bf_locale', l); } catch { /* ignore */ }
  }, []);
  const t = useCallback((key: string, fallback: string, vars?: Record<string, string | number>) =>
    interpolate(dict[locale][key] ?? dict.en[key] ?? fallback, vars), [locale]);
  const value = useMemo(() => ({ locale, dir, setLocale, t }), [locale, dir, setLocale, t]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  const c = useContext(I18nCtx);
  if (!c) throw new Error('useI18n outside provider');
  return c;
}
export const useT = () => useI18n().t;
